// Shared GET cache with in-flight request deduplication.
const cachedResponses = new Map();
const inFlightRequests = new Map();
const queryGenerations = new Map();

export const API_QUERY_KEYS = {
  loginUser: "loginUser",
  userList: "userList",
  userDetail: (userId) => `userDetail:${userId}`,
  questionList: "questionList",
  questionDetail: (questionId) => `questionDetail:${questionId}`,
  adminUserList: "adminUserList",
  companyList: "companyList",
};

const getGeneration = (key) => queryGenerations.get(key) ?? 0;

// Failed requests are never cached, so a later user action can retry them.
export const getCachedQuery = (key, request) => {
  if (cachedResponses.has(key)) {
    return Promise.resolve(cachedResponses.get(key));
  }

  const generation = getGeneration(key);
  const activeRequest = inFlightRequests.get(key);
  if (activeRequest?.generation === generation) {
    return activeRequest.promise;
  }

  let promise;
  promise = Promise.resolve()
    .then(request)
    .then((response) => {
      if (getGeneration(key) === generation) {
        cachedResponses.set(key, response);
      }
      return response;
    })
    .finally(() => {
      if (inFlightRequests.get(key)?.promise === promise) {
        inFlightRequests.delete(key);
      }
    });

  inFlightRequests.set(key, { generation, promise });
  return promise;
};

// Invalidation ensures that the next GET reflects a successful mutation.
export const invalidateApiQueries = (...keys) => {
  keys.flat().forEach((key) => {
    cachedResponses.delete(key);
    queryGenerations.set(key, getGeneration(key) + 1);
  });
};

export const clearApiQueryCache = () => {
  cachedResponses.clear();
  inFlightRequests.clear();
  queryGenerations.clear();
};
